import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableRow from "@material-ui/core/TableRow";
import { CombosListFragment } from "@saleor/combos/types/CombosListFragment";
import { ComboListUrlSortField } from "@saleor/combos/urls";
import Checkbox from "@saleor/components/Checkbox";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import StatusLabel from "@saleor/components/StatusLabel";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TableHead from "@saleor/components/TableHead";
import TablePagination from "@saleor/components/TablePagination";
import { maybe, renderCollection } from "@saleor/misc";
import { ListActions, ListProps, SortPage } from "@saleor/types";
import { getArrowDirection } from "@saleor/utils/sort";
import moment from "moment-timezone";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

const useStyles = makeStyles(
  theme => ({
    [theme.breakpoints.up("lg")]: {
      colDef: {
        width: "auto",
        textAlign: "left"
      },
      colProduct: {
        width: 400,
        textAlign: "left"
      }
    }
  }),
  { name: "ComboList" }
);

interface ComboListProps
  extends ListProps,
    ListActions,
    SortPage<ComboListUrlSortField> {
  combos?: CombosListFragment[];
}

const numberOfColumns = 7;

const ComboList: React.FC<ComboListProps> = ({
  combos,
  disabled,
  settings,
  sort,
  pageInfo,
  isChecked,
  selected,
  toggle,
  toggleAll,
  toolbar,
  onNextPage,
  onPreviousPage,
  onUpdateListSettings,
  onRowClick,
  onSort
}) => {
  const classes = useStyles({});
  const intl = useIntl();

  return (
    <ResponsiveTable>
      <TableHead
        colSpan={numberOfColumns}
        selected={selected}
        disabled={disabled}
        items={combos}
        toggleAll={toggleAll}
        toolbar={toolbar}
      >
        <TableCellHeader
          className={classes.colDef}
          direction={
            sort.sort === ComboListUrlSortField.name
              ? getArrowDirection(sort.asc)
              : undefined
          }
          onClick={() => onSort(ComboListUrlSortField.name)}
        >
          <FormattedMessage defaultMessage="Combo Name" />
        </TableCellHeader>
        <TableCellHeader
          direction={
            sort.sort === ComboListUrlSortField.total_price
              ? getArrowDirection(sort.asc)
              : undefined
          }
          className={classes.colDef}
          onClick={() => onSort(ComboListUrlSortField.total_price)}
        >
          <FormattedMessage
            defaultMessage="Total Price"
            description="combo total_price"
          />
        </TableCellHeader>
        <TableCellHeader className={classes.colProduct}>
          <FormattedMessage
            defaultMessage="Products"
            description="combo Products"
          />
        </TableCellHeader>
        <TableCellHeader
          direction={
            sort.sort === ComboListUrlSortField.enable
              ? getArrowDirection(sort.asc)
              : undefined
          }
          className={classes.colDef}
          onClick={() => onSort(ComboListUrlSortField.enable)}
        >
          <FormattedMessage
            defaultMessage="Is Enable"
            description="combo enable"
          />
        </TableCellHeader>
        <TableCellHeader
          direction={
            sort.sort === ComboListUrlSortField.last_change
              ? getArrowDirection(sort.asc)
              : undefined
          }
          className={classes.colDef}
          onClick={() => onSort(ComboListUrlSortField.last_change)}
        >
          <FormattedMessage
            defaultMessage="Update Time"
            description="combo last_change"
          />
        </TableCellHeader>
        <TableCellHeader
          direction={
            sort.sort === ComboListUrlSortField.order_number
              ? getArrowDirection(sort.asc)
              : undefined
          }
          className={classes.colDef}
          onClick={() => onSort(ComboListUrlSortField.order_number)}
        >
          <FormattedMessage
            defaultMessage="Order Number"
            description="combo order_number"
          />
        </TableCellHeader>
      </TableHead>
      <TableFooter>
        <TableRow>
          <TablePagination
            colSpan={numberOfColumns}
            settings={settings}
            hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
            onNextPage={onNextPage}
            onUpdateListSettings={onUpdateListSettings}
            hasPreviousPage={
              pageInfo && !disabled ? pageInfo.hasPreviousPage : false
            }
            onPreviousPage={onPreviousPage}
          />
        </TableRow>
      </TableFooter>
      <TableBody>
        {renderCollection(
          combos,
          combo => {
            const isSelected = combo ? isChecked(combo.id) : false;

            return (
              <TableRow
                className={classes.colDef}
                hover={!!combo}
                onClick={combo ? onRowClick(combo.id) : undefined}
                key={combo ? combo.id : "skeleton"}
                selected={isSelected}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isSelected}
                    disabled={disabled}
                    disableClickPropagation
                    onChange={() => toggle(combo.id)}
                  />
                </TableCell>
                <TableCell className={classes.colDef} data-test="name">
                  {combo && combo.name ? combo.name : <Skeleton />}
                </TableCell>
                <TableCell className={classes.colDef} data-test="name">
                  {combo && combo.totalPrice ? combo.totalPrice : <Skeleton />}
                </TableCell>
                <TableCell className={classes.colProduct} data-test="name">
                  {combo && combo.products ? (
                    combo.products.edges.map((edge, index) =>
                      index < 3
                        ? index === 0
                          ? edge.node.name
                          : "," + edge.node.name
                        : index === 3
                        ? "....."
                        : ""
                    )
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell className={classes.colDef} data-test="name">
                  {maybe(
                    () => (
                      <StatusLabel
                        label={
                          combo.enable
                            ? intl.formatMessage({
                                defaultMessage: "Enable",
                                description: "combo is Enable"
                              })
                            : intl.formatMessage({
                                defaultMessage: "Not Enable",
                                description: "combo is not Enable"
                              })
                        }
                        status={combo.enable ? "success" : "error"}
                      />
                    ),
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell className={classes.colDef} data-test="name">
                  {combo && combo.lastChange ? (
                    moment(combo.lastChange).format("YYYY-MM-DD HH:mm:ss")
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell className={classes.colDef}>
                  {combo && combo.orderNumber ? (
                    combo.orderNumber
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
              </TableRow>
            );
          },
          () => (
            <TableRow>
              <TableCell colSpan={numberOfColumns}>
                <FormattedMessage defaultMessage="No combos found" />
              </TableCell>
            </TableRow>
          )
        )}
      </TableBody>
    </ResponsiveTable>
  );
};

ComboList.displayName = "ComboList";
export default ComboList;
